import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";

import { api } from "@/lib/api";
import { dateTime } from "@/lib/format";

export const NotificationBell = () => {
  const [state, setState] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const res = await api.get("/notifications");
      setState(res.data);
    } catch { /* signed out or offline */ }
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(load, 60000);
    return () => clearInterval(timer);
  }, [load]);

  const openItem = async (item) => {
    setOpen(false);
    await api.post("/notifications/read", { ids: [item.id] }).catch(() => {});
    await load();
    if (item.link) navigate(item.link);
  };

  return (
    <div className="relative">
      <button data-testid="topbar-notifications" onClick={() => { setOpen(!open); load(); }}
              className="relative rounded-lg p-2 text-muted-foreground transition-colors duration-150 hover:bg-muted hover:text-foreground">
        <Bell className="h-4 w-4" />
        {state.unread > 0 && (
          <span data-testid="notification-count"
                className="absolute -right-0.5 -top-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-primary px-1 text-[9px] font-bold text-primary-foreground">
            {state.unread}
          </span>
        )}
      </button>
      {open && (
        <div data-testid="notification-panel"
             className="absolute right-0 top-11 z-50 w-80 overflow-hidden rounded-xl border border-border bg-card shadow-xl">
          <div className="flex items-center justify-between border-b border-border px-3 py-2">
            <span className="text-[12.5px] font-semibold text-foreground">Notifications</span>
            <button data-testid="notification-mark-all"
                    onClick={async () => { await api.post("/notifications/read", { all: true }); load(); }}
                    className="text-[11px] text-primary hover:underline">Mark all read</button>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {state.items.length === 0 && (
              <div className="px-3 py-8 text-center text-[11.5px] text-muted-foreground"
                   data-testid="notification-empty">
                Nothing needs you right now.
              </div>
            )}
            {state.items.map((n) => (
              <button key={n.id} onClick={() => openItem(n)} data-testid={`notification-${n.id}`}
                      className={`block w-full border-b border-border px-3 py-2.5 text-left transition-colors duration-150 hover:bg-muted ${n.read ? "" : "bg-primary/5"}`}>
                <div className="text-[12.5px] font-medium text-foreground">{n.title}</div>
                <div className="mt-0.5 line-clamp-2 text-[11px] text-muted-foreground">{n.body}</div>
                <div className="mt-0.5 text-[10px] text-muted-foreground">{dateTime(n.created_at)}</div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
